import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  BookOpen, Feather, Baby, ScrollText, Heart, Wand2, 
  Skull, Clock, Sparkles, PenLine, FileText, GraduationCap,
  Mic2, Flame, BookMarked, Users, Clapperboard, Theater, NotebookPen
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

// Genre icons and descriptions
const GENRE_DATA = {
  "General Fiction": {
    icon: BookOpen,
    description: "Stories that explore the human experience in all its complexity.",
    color: "bg-brand-primary/10 text-brand-primary"
  },
  "Literary Fiction": {
    icon: Feather,
    description: "Character-driven narratives with artistic prose and deeper themes.",
    color: "bg-purple-100 text-purple-700"
  },
  "Children's": {
    icon: Baby,
    description: "Magical stories for young readers. Picture books, early readers, and chapter books.",
    color: "bg-pink-100 text-pink-600"
  },
  "Poetry": {
    icon: ScrollText,
    description: "The art of words. Free verse, sonnets, haiku, and everything in between.",
    color: "bg-indigo-100 text-indigo-700"
  },
  "Romance": {
    icon: Heart,
    description: "Love stories that warm the heart and explore human connection.",
    color: "bg-red-100 text-red-500"
  },
  "Fantasy": {
    icon: Wand2,
    description: "Worlds of magic, mythical creatures, and epic adventures.",
    color: "bg-violet-100 text-violet-700"
  },
  "Science Fiction": {
    icon: Sparkles,
    description: "Explore the future, technology, and the unknown reaches of space.",
    color: "bg-cyan-100 text-cyan-700"
  },
  "Horror": {
    icon: Skull,
    description: "Tales that chill the spine and explore our deepest fears.",
    color: "bg-gray-200 text-gray-700"
  },
  "Historical Fiction": {
    icon: Clock,
    description: "Stories set in the past, bringing history to vivid life.",
    color: "bg-amber-100 text-amber-700"
  },
  "Mystery/Thriller": {
    icon: Flame,
    description: "Suspenseful tales of crime, detection, and heart-pounding tension.",
    color: "bg-orange-100 text-orange-700"
  },
  "Creative Non-Fiction": {
    icon: FileText,
    description: "True stories told with literary flair. Essays, memoirs, and personal narratives.",
    color: "bg-teal-100 text-teal-700"
  },
  "Memoir": {
    icon: BookMarked,
    description: "Personal stories from real life. Share your journey and experiences.",
    color: "bg-emerald-100 text-emerald-700"
  },
  "Young Adult": {
    icon: GraduationCap,
    description: "Stories for teens exploring identity, first love, and growing up.",
    color: "bg-blue-100 text-blue-700"
  },
  "Flash Fiction": {
    icon: Flame,
    description: "Complete stories in 1,000 words or less. Every word counts.",
    color: "bg-rose-100 text-rose-600"
  },
  "Short Story": {
    icon: PenLine,
    description: "Self-contained narratives perfect for a single sitting.",
    color: "bg-brand-gold/20 text-brand-gold"
  },
  "Novella": {
    icon: BookOpen,
    description: "Longer than a short story, shorter than a novel. The perfect middle ground.",
    color: "bg-lime-100 text-lime-700"
  },
  "Screenwriting": {
    icon: Clapperboard,
    description: "Scripts for film and television. Bring your cinematic vision to life.",
    color: "bg-sky-100 text-sky-700"
  },
  "Playwriting": {
    icon: Theater,
    description: "Scripts for the stage. Drama, comedy, and everything that lives in live performance.",
    color: "bg-fuchsia-100 text-fuchsia-700"
  },
  "Essay": {
    icon: NotebookPen,
    description: "Personal essays, opinion pieces, and thought-provoking written explorations.",
    color: "bg-stone-200 text-stone-700"
  },
  "Spoken Word": {
    icon: Mic2,
    description: "Performance poetry and spoken word pieces meant to be heard aloud.",
    color: "bg-yellow-100 text-yellow-700"
  }
};

const SectionsPage = () => {
  const { api, user } = useAuth();
  const [genreCounts, setGenreCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGenreCounts = async () => {
      try {
        // Get all stories and count by genre
        const res = await api.get('/stories?limit=500');
        const counts = {};
        res.data.forEach(story => {
          counts[story.genre] = (counts[story.genre] || 0) + 1;
        });
        setGenreCounts(counts);
      } catch (error) {
        // Non-critical error suppressed
      } finally {
        setLoading(false);
      }
    };

    fetchGenreCounts();
  }, [api]);

  const genres = Object.keys(GENRE_DATA);

  return (
    <div className="min-h-screen" data-testid="sections-page">
      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-primary/5 to-brand-gold/5" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="overline text-brand-gold mb-4 block">All Writers Welcome</span>
            <h1 className="font-serif text-4xl lg:text-5xl font-semibold text-text-primary mb-6">
              Find Your Writing Home
            </h1>
            <p className="text-xl text-text-secondary leading-relaxed mb-8">
              Poetry, fiction, screenplays, essays, spoken word, and more —
              there's a place for every kind of writer here. Every voice matters. Every piece deserves to be shared.
            </p>
            {!user && (
              <Link to="/register" data-testid="join-button">
                <Button className="btn-accent text-lg px-8 py-6 gap-2">
                  <Users className="w-5 h-5" />
                  Join Our Community
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Featured Sections */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl font-semibold text-text-primary mb-4">
              Browse by Genre
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Click any section to explore works from that genre, or start writing your own.
            </p>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                <Skeleton key={i} className="h-48 rounded-xl" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {genres.map((genre) => {
                const data = GENRE_DATA[genre];
                const Icon = data.icon;
                const count = genreCounts[genre] || 0;
                
                return (
                  <Link 
                    key={genre}
                    to={`/stories?genre=${encodeURIComponent(genre)}`}
                    className="group"
                    data-testid={`genre-${genre.toLowerCase().replace(/[^a-z]/g, '-')}`}
                  >
                    <Card className="h-full bg-white border-brand-primary/10 overflow-hidden card-hover">
                      <CardContent className="p-6">
                        <div className={`w-14 h-14 rounded-xl ${data.color} flex items-center justify-center mb-4 transition-transform group-hover:scale-110`}>
                          <Icon className="w-7 h-7" />
                        </div>
                        <h3 className="font-serif text-xl font-semibold text-text-primary mb-2 group-hover:text-brand-accent transition-colors">
                          {genre}
                        </h3>
                        <p className="text-text-secondary text-sm leading-relaxed mb-4">
                          {data.description}
                        </p>
                        <div className="flex items-center justify-between pt-4 border-t border-brand-primary/10">
                          <span className="text-sm text-text-muted">
                            {count} {count === 1 ? 'story' : 'stories'}
                          </span>
                          <span className="text-sm text-brand-accent font-medium group-hover:underline">
                            Explore →
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Call to Action for Writers */}
      <section className="py-20 bg-surface-alt">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl font-semibold text-text-primary mb-6">
            Don't See Your Genre?
          </h2>
          <p className="text-lg text-text-secondary mb-8 max-w-2xl mx-auto">
            We're always growing! Start writing in any genre and help us build a diverse, 
            inclusive library of stories. Your unique voice is exactly what we need.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {user ? (
              <Link to="/write" data-testid="start-writing-button">
                <Button className="btn-accent gap-2">
                  <PenLine className="w-4 h-4" />
                  Start Writing
                </Button>
              </Link>
            ) : (
              <Link to="/register" data-testid="join-now-button">
                <Button className="btn-accent gap-2">
                  <Users className="w-4 h-4" />
                  Join Now—It's Free
                </Button>
              </Link>
            )}
            <Link to="/challenges" data-testid="browse-challenges-button">
              <Button variant="outline" className="btn-outline gap-2">
                Browse Challenges
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Welcoming Message */}
      <section className="py-16 bg-brand-dark text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Feather className="w-12 h-12 text-brand-gold mx-auto mb-6" />
          <h2 className="font-serif text-3xl font-semibold mb-6">
            Every Writer Has a Story to Tell
          </h2>
          <p className="text-white/80 text-lg leading-relaxed mb-6">
            Whether you're a seasoned novelist or writing your first poem, 
            you belong here. We believe in craft over competition, 
            feedback over flattery, and community over cliques.
          </p>
          <p className="text-brand-gold font-medium italic">
            "You show me yours, and I'll show you mine. Then collectively, we'll ALL advance our talent."
          </p>
          <p className="text-white/60 text-sm mt-2">— The Collectively Write Philosophy</p>
        </div>
      </section>
    </div>
  );
};

export default SectionsPage;
